// ── Sessions surfaces (Wave 6 — REBUILT as gold-pattern pages) ──────────────
// The thin mock-only session surfaces (product-session-list-surfaces /
// product-session-detail-surfaces / the mock series/invite workflow surfaces)
// are replaced by real pages: SessionsPage (operational list), CalendarPage
// (bounded date-range view), SessionDetailPage (status sections + write flows),
// CreateSeriesPage (preview → create), and ClearSeriesPage (gated/disabled
// placeholder — no backend endpoint). Each is wired via `customBody` so the route
// registration + provenance metadata is preserved while the new page renders.
//
// Component provenance is declared per screen as a SUBSET of what the mapped
// source files actually import + render (asserted by product-route-contracts.test).

import type { ProductScreenContract } from "./product-route-contracts";
export { SessionsPage } from "./sessions/sessions-page";
import { SessionsPage } from "./sessions/sessions-page";
import { CalendarPage } from "./sessions/calendar-page";
import { SessionDetailPage } from "./sessions/session-detail-page";
import { CreateSeriesPage } from "./sessions/create-series-page";
import { ClearSeriesPage } from "./sessions/clear-series-page";

const SESSIONS_OWNER = "VIL-20260608-0021";
const CALENDAR_OWNER = "VIL-20260608-0018";

function focusedDefaults(): Pick<
  ProductScreenContract,
  "requiredViewports" | "screenEvidencePaths" | "mobileEvidencePaths" | "surfaceMode" | "closureState"
> {
  return {
    requiredViewports: ["desktop", "mobile"],
    surfaceMode: "focused",
    closureState: "focused"
  };
}

export function sessionsListScreen(): ProductScreenContract {
  return {
    title: () => "Sessions",
    primaryJob: "Review the role-scoped coaching schedule and open any session for its status and workflows.",
    secondaryJob: "Filter by status/cohort; switch to the calendar view; participants never see the admin schedule.",
    workTitle: "Operational sessions",
    rows: [{ id: "session-1", session: "Session 1", cohort: "Spring Leaders", status: "scheduled" }],
    columns: [
      { key: "session", header: "Session" },
      { key: "cohort", header: "Cohort" },
      { key: "status", header: "Status" }
    ],
    states: ["loading", "empty", "permission-denied", "unavailable", "gate-closed", "error"],
    apiFamilies: ["session-service"],
    roleDeltas:
      "Admin sees global sessions; coach sees assigned; company_admin/cohort_lead read-only; participant DENIED the module (fail-closed).",
    extensionSlots: [
      "session cancel backend blocker",
      "ad hoc session backend blocker",
      "session coach reassignment backend blocker"
    ],
    mobilePosture: "Filter toolbar, count, and the sessions table stack with no horizontal overflow.",
    evidenceRequirement:
      "Playwright must prove the scoped read, participant-denied fail-closed, every honest posture, and the row → detail link.",
    vjalComponents: ["Button","CollectionToolbar","IconCalendar","PageFrame","ProTable"],
    localCompositions: ["sessions focused product surface"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Filter toolbar + the role-scoped sessions table.",
      primaryAction: "Open a session for its status sections and write flows.",
      secondaryPanel: "Calendar view link + status/cohort filters.",
      deferredSurface: "Per-session write flows live on the session detail.",
      gatedControls: "Participant is denied the module; read-only roles see status only."
    },
    ...focusedDefaults(),
    closureOwner: SESSIONS_OWNER,
    customBody: SessionsPage
  } as ProductScreenContract;
}

export function calendarScreen(): ProductScreenContract {
  return {
    title: () => "Calendar",
    primaryJob: "See the role-scoped sessions grouped by day in a bounded date-range view.",
    secondaryJob: "On a narrow screen the days stack as a list; switch to the list view anytime.",
    workTitle: "Session calendar",
    rows: [{ id: "session-1", session: "Session 1", day: "Mar 8" }],
    columns: [
      { key: "session", header: "Session" },
      { key: "day", header: "Day" }
    ],
    states: ["loading", "empty", "permission-denied", "unavailable", "error"],
    apiFamilies: ["session-service"],
    roleDeltas:
      "Same scoping as the sessions list (admin global / coach assigned / company-admin + cohort-lead read-only); participant DENIED.",
    extensionSlots: ["week/month range controls (future)", "drag-reschedule backend blocker"],
    mobilePosture: "Day buckets stack into a single column list with no overflow.",
    evidenceRequirement:
      "Playwright must prove the day-bucketed scoped read, participant-denied fail-closed, and the empty/loading postures.",
    vjalComponents: ["Badge","Card","IconCalendar","PageFrame"],
    localCompositions: ["calendar focused product surface"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Day-grouped session cards in the bounded range.",
      primaryAction: "Open a session from its calendar card.",
      secondaryPanel: "List-view link.",
      deferredSurface: "Range controls and drag-reschedule are future work.",
      gatedControls: "Participant is denied; read-only roles see status only."
    },
    ...focusedDefaults(),
    closureOwner: CALENDAR_OWNER,
    customBody: CalendarPage
  } as ProductScreenContract;
}

export function sessionDetailScreen(): ProductScreenContract {
  return {
    title: () => "Session detail",
    primaryJob:
      "Inspect one session: meeting/Zoom status, recordings, transcript (role-gated), invite log, report status, and metadata.",
    secondaryJob:
      "Staff/coach act in scope (reschedule, lifecycle, send-invites, reassign); dark lanes render honestly; participant denied.",
    workTitle: "Session workspace",
    rows: [{ id: "session-1", section: "Meeting", status: "not created" }],
    columns: [
      { key: "section", header: "Section" },
      { key: "status", header: "Status" }
    ],
    states: ["loading", "empty", "permission-denied", "unavailable", "not-configured", "gate-closed", "error"],
    apiFamilies: ["session-service", "artifact-service"],
    roleDeltas:
      "Admin/coach act in scope; admin-only reassign-coach; company_admin/cohort_lead read-only; participant DENIED; transcript hard-denied for participant + cohort_lead.",
    extensionSlots: ["provider delivery reconciliation", "ad hoc session backend blocker", "post-session email"],
    mobilePosture: "Header, status sections, and modals stack with peer privacy and no overflow.",
    evidenceRequirement:
      "Playwright must prove the role matrix, participant-denied, transcript-denied for participant/cohort_lead, the dark-lane honest postures, and each write confirm.",
    vjalComponents: ["Badge","Button","DetailSurface","Module","ModuleBody","ModuleHead"],
    localCompositions: ["session-detail focused product surface"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Session identity header + role-gated actions.",
      primaryAction: "Run a session workflow (reschedule / lifecycle / send-invites / reassign).",
      secondaryPanel: "Status sections: meeting, recordings, transcript, invite log, report, metadata.",
      deferredSurface: "Reports content is owned by the Reports area (status only here).",
      gatedControls: "Dark lanes render not-configured/unavailable; transcript denied for participant/cohort_lead."
    },
    ...focusedDefaults(),
    closureOwner: SESSIONS_OWNER,
    customBody: SessionDetailPage
  } as ProductScreenContract;
}

export function createSeriesScreen(): ProductScreenContract {
  return {
    title: () => "Create session series",
    primaryJob: "Preview the generated sessions (no write), then create them; Zoom scheduling stays dark.",
    secondaryJob: "SA/Admin create within scope; coach only when approved (gated); read-only roles can't; participant denied.",
    workTitle: "Create session series",
    rows: [{ id: "preview-1", session: "Session 1", status: "preview" }],
    columns: [
      { key: "session", header: "Session" },
      { key: "status", header: "Status" }
    ],
    states: ["gated", "permission-denied", "empty", "gate-closed", "error"],
    apiFamilies: ["session-service"],
    roleDeltas:
      "SA/Admin allowed; coach gated behind operator approval (default OFF); company_admin/cohort_lead can't create; participant DENIED.",
    extensionSlots: ["custom session cadence (future)", "duration/timezone comparison preview"],
    mobilePosture: "Date/time/timezone inputs, the preview table, and confirm stack with no overflow.",
    evidenceRequirement:
      "Playwright must prove preview (no write) → create (idempotency-keyed), the Zoom-dark honest copy, and coach-gated/role denials.",
    vjalComponents: ["Alert","Button","Input","Module","ModuleBody","ModuleHead","PageFrame"],
    localCompositions: ["cohort-session-series focused product surface"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Start date/time/timezone + Preview.",
      primaryAction: "Preview, then Create series.",
      secondaryPanel: "The previewed (not-yet-created) sessions table.",
      deferredSurface: "Zoom meeting scheduling is a closed lane (honest pending).",
      gatedControls: "Coach is gated behind approval; read-only roles denied."
    },
    ...focusedDefaults(),
    closureOwner: SESSIONS_OWNER,
    customBody: CreateSeriesPage
  } as ProductScreenContract;
}

export function clearSeriesScreen(): ProductScreenContract {
  return {
    title: () => "Clear session series",
    primaryJob: "Clears the cohort's FUTURE sessions (FR-SESS-002, NOW LIVE) — SA + the cohort's coach only.",
    secondaryJob:
      "KEEP + DETACH: past sessions and the already-sent invites/recordings/reports are kept and detached, never deleted. Requires an explicit destructive confirm + a UUID Idempotency-Key.",
    workTitle: "Clear session series",
    rows: [{ id: "clear-1", state: "ready", reason: "confirm + Idempotency-Key required" }],
    columns: [
      { key: "state", header: "State" },
      { key: "reason", header: "Reason" }
    ],
    states: ["empty", "permission-denied", "gate-closed", "unavailable", "error", "ready"],
    apiFamilies: ["session-service (POST /cohorts/:id/sessions/clear, SA + coach-in-scope)"],
    roleDeltas:
      "Super-admin (any cohort) + the cohort's coach (assigned cohorts only) reach + act; admin/company_admin/cohort_lead/participant denied (fail-closed).",
    extensionSlots: ["additional clear-as-of options (additive)"],
    mobilePosture: "The warning, the confirm + reason inputs, and the honest result/error stack with no overflow.",
    evidenceRequirement:
      "Playwright must prove the confirm action calls the route with an Idempotency-Key, the honest cleared/kept-and-detached result, and denial for non-(SA/coach) roles.",
    vjalComponents: ["Alert","Button","Input","Module","ModuleBody","ModuleHead","PageFrame"],
    localCompositions: ["clear-series focused product surface"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Warning that this clears future sessions and keeps-and-detaches the rest.",
      primaryAction: "Clear future sessions — enabled only after an explicit destructive confirm.",
      secondaryPanel: "Optional reason + the honest cleared/detached result.",
      deferredSurface: "Additional as-of/clear options land additively.",
      gatedControls: "SA + the cohort's coach only; every other role denied."
    },
    ...focusedDefaults(),
    closureOwner: SESSIONS_OWNER,
    customBody: ClearSeriesPage
  } as ProductScreenContract;
}
